import { useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { Button } from '../../ui/Button/Button'
import { usePatientDialog } from '../patients/use-patient-dialog'
import styles from './SettingsSection.module.css'

/**
 * A short question that needs an answer before anything changes. The safe
 * choice has focus, Escape picks it, and focus returns to the opener.
 */
export function SettingsConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  busy = false,
  busyLabel,
  error,
  danger = false,
}: {
  title: string
  children: ReactNode
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
  /** The confirmed change is in flight: the dialog stays until it settles. */
  busy?: boolean
  busyLabel?: string
  /** Why the confirmed change did not happen, shown in the dialog. */
  error?: string | null
  /** The change removes something; the confirm button says so in red. */
  danger?: boolean
}) {
  const dialog = useRef<HTMLElement>(null)
  const titleId = useId()
  const descriptionId = useId()
  usePatientDialog(dialog, { onClose: onCancel, locked: busy })

  return createPortal(
    <div className={styles.backdrop} role="presentation">
      <section
        ref={dialog}
        className={`${styles.dialog} ${styles.confirmDialog}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <div className={styles.confirmBody}>
          <h2 id={titleId}>{title}</h2>
          <div id={descriptionId}>{children}</div>
          {error ? (
            <p className={styles.fieldError} role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <div className={styles.dialogFooter}>
          <div className={styles.footerActions}>
            <Button
              type="button"
              variant="secondary"
              data-autofocus
              disabled={busy}
              onClick={onCancel}
            >
              {cancelLabel}
            </Button>
            <Button
              type="button"
              className={danger ? styles.dangerSolid : undefined}
              aria-disabled={busy || undefined}
              onClick={() => {
                if (!busy) onConfirm()
              }}
            >
              {busy ? (busyLabel ?? confirmLabel) : confirmLabel}
            </Button>
          </div>
        </div>
      </section>
    </div>,
    document.body
  )
}
